import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../components/Icon';

interface Comparison {
  /** Der Unterschied zum Vergleichszeitraum, in Prozentpunkten der Menge. */
  percent: number;
  /** Woran verglichen wird -- steht neben der Zahl, nicht im Beipackzettel. */
  label: string;
  /**
   * Ob "mehr" gut ist. Mehr erfasste Zeit ist gut, mehr ueberschrittene
   * Fristen nicht -- ohne diese Angabe waere jeder Anstieg gruen.
   */
  moreIsBetter: boolean;
}

interface StatTileProps {
  label: string;
  value: string;
  hint?: string;
  tone?: 'danger' | 'success';
  comparison?: Comparison;
  /** Wohin die Kachel fuehrt -- in die Liste, die genau diese Zahl zaehlt. */
  to?: string;
  /** Die Einschraenkung der Zielliste. Werte, wie die Adresse sie traegt --
   *  Zeichenketten und Schalter; ``unresolved`` etwa ist einer. */
  search?: Record<string, string | boolean>;
  children?: ReactNode;
}

/**
 * Eine Kennzahl auf der Startseite -- F5-18, K-121.
 *
 * **Eine Zahl allein sagt "zwoelf".** Zwoelf ist erst dann eine Aussage,
 * wenn daneben steht, dass es in der Woche davor neunzehn waren -- und wenn
 * ein Klick darauf die zwoelf Vorgaenge zeigt, um die es geht. Beides ist
 * hier eingebaut.
 *
 * **Der Vergleich erscheint nur, wo er berechenbar ist.** Eine Kachel ohne
 * ``comparison`` zeigt keinen -- und nicht eine Null, die aussaehe wie
 * "unveraendert". Eine erfundene Richtung ist in einer Auswertung, die in
 * einer Pruefung vorgelegt wird, schlimmer als eine fehlende.
 *
 * **Die Richtung traegt die Aussage nicht allein.** Der Pfeil steht neben der
 * Zahl mit ihrem Vorzeichen, und daneben steht, wogegen verglichen wurde. Wer
 * Farben nicht unterscheiden kann, liest dieselbe Aussage.
 *
 * **Die Kachel ist ein Verweis, wo es eine Liste dazu gibt.** Als ``<Link>``
 * und nicht als Kasten mit Klickbehandlung: So ist sie mit der Tastatur
 * erreichbar, wird als Verweis angesagt und laesst sich in einem neuen
 * Reiter oeffnen.
 */
export function StatTile({
  label,
  value,
  hint,
  tone,
  comparison,
  to,
  search,
  children,
}: StatTileProps) {
  const { t } = useTranslation();

  const body = (
    <>
      <p className="kpi__label">{label}</p>
      <p className={tone ? `kpi__value kpi__value--${tone}` : 'kpi__value'}>{value}</p>
      {comparison ? <Delta {...comparison} /> : null}
      {hint ? <span className="kpi__hint">{hint}</span> : null}
      {children}
    </>
  );

  if (to === undefined) return <section className="card kpi">{body}</section>;

  return (
    <Link
      to={to}
      search={search}
      className="card kpi card--actionable"
      aria-label={t('dashboard.tileLink', { label })}
    >
      {body}
    </Link>
  );
}

/** Die Richtungszeile unter der Zahl. */
function Delta({ percent, label, moreIsBetter }: Comparison) {
  const { t } = useTranslation();

  // Unter einem Prozentpunkt ist kein Trend, sondern Rauschen. Ein Pfeil
  // dafuer waere eine Aussage, die die Daten nicht hergeben.
  if (Math.abs(percent) < 1) {
    return (
      <span className="kpi__delta">
        {t('dashboard.deltaFlat')} {label}
      </span>
    );
  }

  const up = percent > 0;
  const good = up === moreIsBetter;
  const rounded = Math.round(Math.abs(percent));

  return (
    <span className={`kpi__delta kpi__delta--${good ? 'up' : 'down'}`}>
      <Icon name={up ? 'arrowUp' : 'arrowDown'} />
      {t(up ? 'dashboard.deltaUp' : 'dashboard.deltaDown', { percent: rounded })} {label}
    </span>
  );
}
